import { createFileRoute } from "@tanstack/react-router";
import { ArrowUpRight, Check, Download, Minus } from "lucide-react";
import { BookingFab } from "#/components/booking-fab";
import { LeadForm } from "#/components/lead-form";
import { LogoMark } from "#/components/logo";
import { Accordion, ForwardArrow } from "#/components/ui";
import { VariantChrome } from "#/components/variant-chrome";
import { useI18n } from "#/lib/i18n";
import { getDefaultTraining } from "#/lib/training";

export const Route = createFileRoute("/hero-a")({
	head: () => ({ meta: [{ name: "robots", content: "noindex, nofollow" }] }),
	loader: () => getDefaultTraining(),
	component: HeroA,
});

/** Editorial numbered index: 01 in gold, hairline rule, spaced small-caps label. */
function SectionIndex({
	index,
	children,
	tone = "light",
}: {
	index: string;
	children: React.ReactNode;
	tone?: "light" | "dark";
}) {
	return (
		<div className="flex items-center gap-4">
			<span className="index-num font-display text-sm font-bold text-gold">
				{index}
			</span>
			<span
				aria-hidden
				className={`h-px w-10 ${tone === "dark" ? "bg-white/25" : "bg-line"}`}
			/>
			<span
				className={`text-[0.7rem] font-bold uppercase tracking-[0.22em] ${
					tone === "dark" ? "text-white/70" : "text-muted"
				}`}
			>
				{children}
			</span>
		</div>
	);
}

function HeroA() {
	const { t } = useI18n();
	const training = Route.useLoaderData();
	const format = t.hero.eyebrow.split("·").map((s) => s.trim());

	return (
		<div className="overflow-x-clip bg-canvas">
			<VariantChrome active="a" />

			{/* ---------- hero ---------- */}
			<section className="mx-auto grid max-w-6xl items-center gap-14 px-5 pb-16 pt-28 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:gap-10 lg:pt-32">
				{/* copy */}
				<div className="animate-fade-up">
					{/* kicker — a hairline rule + spaced small caps, no pill/dots */}
					<div className="flex items-center gap-4">
						<span aria-hidden className="h-px w-12 bg-navy/25" />
						<span className="flex flex-wrap gap-x-4 gap-y-1 text-[0.7rem] font-bold uppercase tracking-[0.22em] text-muted">
							{format.map((f) => (
								<span key={f}>{f}</span>
							))}
						</span>
					</div>

					<h1 className="mt-7 font-display font-bold leading-[1.02] text-navy text-[clamp(2.7rem,7.5vw,5rem)]">
						{t.hero.titleLead}{" "}
						<span className="relative inline-block text-teal">
							{t.hero.titleHighlight}
							<span
								aria-hidden
								className="absolute inset-x-0 -bottom-1 h-0.75 rounded-full bg-gold"
							/>
						</span>
					</h1>

					<p className="mt-7 max-w-xl text-lg leading-relaxed text-muted">
						{t.hero.subtitle}
					</p>

					<div className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center">
						<a
							href="#register"
							className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-navy px-8 py-4 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-teal sm:w-auto"
						>
							{t.hero.ctaPrimary}
							<ForwardArrow className="h-4 w-4 transition-transform rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1" />
						</a>
						<a
							href="#course"
							className="group inline-flex items-center justify-center gap-1.5 px-3 py-4 text-base font-bold text-navy underline-offset-4 hover:underline"
						>
							{t.hero.ctaSecondary}
							<ArrowUpRight className="h-4 w-4 rtl:-scale-x-100" />
						</a>
					</div>

					<div className="mt-10 flex items-center gap-3 text-sm text-muted">
						<span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-teal/12 text-teal">
							<Check className="h-3.5 w-3.5" strokeWidth={3} />
						</span>
						{t.hero.trust}
					</div>
				</div>

				{/* portrait — plain frame, solid teal block offset behind */}
				<div className="relative mx-auto w-full max-w-sm animate-fade-up [animation-delay:120ms] lg:max-w-none">
					<div
						aria-hidden
						className="absolute -bottom-5 h-full w-full rounded-4xl bg-teal/15 ltr:-right-5 rtl:-left-5"
					/>
					<div className="relative overflow-hidden rounded-4xl bg-navy shadow-lift">
						<img
							src="/team/ayoub-portrait.webp"
							alt={t.hero.portraitAlt}
							width={700}
							height={840}
							className="aspect-5/6 w-full object-cover object-top"
						/>
						<div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-navy via-navy/40 to-transparent p-6 pt-16">
							<div className="font-display text-xl font-bold text-white">
								{t.trainer.name}
							</div>
							<div className="text-sm text-teal-100">{t.trainer.role}</div>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- stats band — editorial ledger between hairlines ---------- */}
			<section className="mx-auto max-w-6xl px-5 sm:px-8">
				<dl className="grid grid-cols-2 gap-y-8 border-y border-line py-10 sm:grid-cols-5">
					{t.stats.map((s, i) => (
						<div
							key={s.label}
							className={`px-4 max-sm:last:col-span-2 sm:px-6 ${
								i > 0 ? "sm:border-s sm:border-line" : ""
							}`}
						>
							<dt className="font-display text-4xl font-bold text-navy lg:text-5xl">
								{s.value}
							</dt>
							<dd className="mt-1.5 text-xs leading-snug text-muted">
								{s.label}
							</dd>
						</div>
					))}
				</dl>
			</section>

			{/* ---------- pains ---------- */}
			<section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-20 lg:py-24">
				<div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
					<div className="lg:sticky lg:top-28 lg:self-start">
						<SectionIndex index="01">{t.pains.eyebrow}</SectionIndex>
						<h2 className="mt-6 font-display text-3xl font-bold leading-[1.1] text-navy sm:text-4xl lg:text-[2.75rem]">
							{t.pains.heading}
						</h2>
						<p className="mt-5 max-w-sm text-base leading-relaxed text-muted">
							{t.pains.sub}
						</p>
					</div>

					<div>
						<ol className="border-t border-line">
							{t.pains.items.map((pain, i) => (
								<li
									key={pain.title}
									className="group grid grid-cols-[auto_1fr] gap-x-5 gap-y-1 border-b border-line py-6 sm:gap-x-8 sm:py-7"
								>
									<span className="index-num font-display text-2xl font-bold text-line transition-colors group-hover:text-gold sm:text-3xl">
										{`0${i + 1}`}
									</span>
									<div>
										<h3 className="font-display text-lg font-bold text-navy sm:text-xl">
											{pain.title}
										</h3>
										<p className="mt-1.5 text-sm leading-relaxed text-muted">
											{pain.text}
										</p>
									</div>
								</li>
							))}
						</ol>

						<div className="mt-8 flex items-start gap-4 bg-navy p-6 sm:items-center sm:p-7">
							<span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-teal text-white">
								<Check className="h-4 w-4" strokeWidth={3} />
							</span>
							<p className="font-display text-base font-bold leading-snug text-white sm:text-lg">
								{t.pains.solution}
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- pillars (solution / course) ---------- */}
			<section
				id="course"
				className="scroll-mt-24 border-y border-line bg-sand py-16 sm:py-20 lg:py-24"
			>
				<div className="mx-auto max-w-6xl px-5 sm:px-8">
					<div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
						<div>
							<SectionIndex index="02">{t.pillars.eyebrow}</SectionIndex>
							<h2 className="mt-6 max-w-xl font-display text-3xl font-bold leading-[1.1] text-navy sm:text-4xl lg:text-[2.75rem]">
								{t.pillars.heading}
							</h2>
						</div>
						<p className="max-w-xs text-sm leading-relaxed text-muted">
							{t.pillars.sub}
						</p>
					</div>

					<div className="mt-14 grid gap-px overflow-hidden border border-line bg-line md:grid-cols-3">
						{t.pillars.items.map((pillar, i) => (
							<article
								key={pillar.title}
								className="group flex flex-col bg-canvas p-8 transition-colors hover:bg-white lg:p-10"
							>
								<div className="flex items-baseline justify-between">
									<span className="index-num font-display text-5xl font-bold text-gold">
										{`0${i + 1}`}
									</span>
									<span
										aria-hidden
										className="h-px w-10 bg-line transition-all group-hover:w-16 group-hover:bg-gold"
									/>
								</div>
								<h3 className="mt-12 font-display text-xl font-bold text-navy lg:text-2xl">
									{pillar.title}
								</h3>
								<p className="mt-3 text-sm leading-relaxed text-muted">
									{pillar.text}
								</p>
							</article>
						))}
					</div>

					{/* technical-sheet downloads */}
					<div className="mt-8 flex flex-col items-start gap-4 border-s-2 border-gold ps-6 sm:flex-row sm:items-center sm:justify-between">
						<span className="text-sm font-bold text-navy">
							{t.pillars.downloadLabel}
						</span>
						<div className="flex flex-wrap items-center gap-3">
							<a
								href="/downloads/fiche-technique-ar.pdf"
								download
								className="inline-flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal"
							>
								<Download className="h-4 w-4" />
								عربية (AR)
							</a>
							<a
								href="/downloads/fiche-technique-fr.pdf"
								download
								className="inline-flex items-center gap-2 rounded-full border border-line bg-canvas px-5 py-2.5 text-sm font-bold text-navy transition-colors hover:border-navy/30 hover:bg-white"
							>
								<Download className="h-4 w-4" />
								Français (FR)
							</a>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- trainer ---------- */}
			<section id="trainer" className="scroll-mt-24 py-16 sm:py-20 lg:py-24">
				<div className="mx-auto grid max-w-6xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-start lg:gap-16">
					{/* portrait column */}
					<div className="relative order-2 lg:order-1 lg:sticky lg:top-28">
						<div
							aria-hidden
							className="absolute -bottom-4 h-full w-full rounded-4xl border-2 border-gold/40 ltr:-right-4 rtl:-left-4"
						/>
						<div className="relative overflow-hidden rounded-4xl bg-navy shadow-lift">
							<img
								src="/team/ayoub-avatar.webp"
								alt={t.trainer.avatarAlt}
								width={460}
								height={460}
								className="aspect-square w-full object-cover"
							/>
							<div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-navy via-navy/60 to-transparent p-6 pt-16">
								<div className="font-display text-xl font-bold text-white">
									{t.trainer.name}
								</div>
								<div className="text-sm text-teal-100">{t.trainer.role}</div>
							</div>
						</div>

						<div className="mt-8 flex items-baseline gap-4 border-t border-line pt-6">
							<span className="index-num font-display text-4xl font-bold text-gold">
								+10K
							</span>
							<span className="text-xs leading-snug text-muted">
								{t.trainer.statLabel}
							</span>
						</div>
						<p className="mt-4 text-sm leading-relaxed text-muted">
							{t.trainer.bio}
						</p>
					</div>

					{/* copy + why accordion */}
					<div className="order-1 lg:order-2">
						<SectionIndex index="03">{t.trainer.eyebrow}</SectionIndex>
						<h2 className="mt-6 font-display text-3xl font-bold leading-[1.1] text-navy sm:text-4xl lg:text-[2.75rem]">
							{t.trainer.heading}
						</h2>
						<p className="mt-5 max-w-xl text-base leading-relaxed text-muted">
							{t.trainer.sub}
						</p>
						<div className="mt-8">
							<Accordion items={t.trainer.why} />
						</div>
					</div>
				</div>
			</section>

			{/* ---------- outcomes (audience / objectives / practice) ---------- */}
			<section
				id="learn"
				className="scroll-mt-24 border-y border-line bg-sand py-16 sm:py-20 lg:py-24"
			>
				<div className="mx-auto max-w-6xl px-5 sm:px-8">
					<SectionIndex index="04">{t.outcomes.audienceHeading}</SectionIndex>

					{/* audience row — inline list separated by dashes */}
					<ul className="mt-6 flex flex-wrap items-center gap-x-2 gap-y-3">
						{t.outcomes.audience.map((item) => (
							<li
								key={item}
								className="inline-flex items-center gap-2 border border-line bg-canvas px-4 py-2 text-sm font-semibold text-navy"
							>
								<Minus
									aria-hidden
									className="h-3.5 w-3.5 flex-none text-gold"
								/>
								{item}
							</li>
						))}
					</ul>

					<div className="mt-12 grid gap-6 lg:grid-cols-[1.25fr_0.75fr]">
						{/* objectives */}
						<div className="bg-navy p-8 sm:p-10">
							<SectionIndex index="05" tone="dark">
								{t.outcomes.objectivesHeading}
							</SectionIndex>
							<ul className="mt-8 flex flex-col divide-y divide-white/10">
								{t.outcomes.objectives.map((item, i) => (
									<li
										key={item}
										className="flex items-start gap-5 py-4 first:pt-0"
									>
										<span className="index-num font-display text-lg font-bold text-gold">
											{`0${i + 1}`}
										</span>
										<span className="text-sm leading-relaxed text-white/80">
											{item}
										</span>
									</li>
								))}
							</ul>
						</div>

						{/* practice pull-figure */}
						<div className="flex flex-col justify-center border-2 border-gold/30 bg-gold-50 p-8 sm:p-10">
							<div className="font-display text-6xl font-bold leading-none text-gold-600 lg:text-7xl">
								{t.outcomes.practiceValue}
							</div>
							<p className="mt-5 text-sm leading-relaxed text-ink/70">
								{t.outcomes.practiceNote}
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* ---------- faq ---------- */}
			<section id="faq" className="scroll-mt-24 py-16 sm:py-20 lg:py-24">
				<div className="mx-auto grid max-w-6xl gap-10 px-5 sm:px-8 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16">
					<div className="lg:sticky lg:top-28 lg:self-start">
						<SectionIndex index="06">{t.faq.eyebrow}</SectionIndex>
						<h2 className="mt-6 font-display text-3xl font-bold leading-[1.1] text-navy sm:text-4xl lg:text-[2.75rem]">
							{t.faq.heading}
						</h2>
						<p className="mt-5 text-sm leading-relaxed text-muted">
							{t.faq.sub}
						</p>
					</div>
					<div>
						<Accordion items={t.faq.items} />
					</div>
				</div>
			</section>

			{/* ---------- register ---------- */}
			<section
				id="register"
				className="scroll-mt-24 border-t border-line bg-navy"
			>
				<div className="mx-auto grid max-w-6xl lg:grid-cols-[1.1fr_0.9fr]">
					<div className="px-5 py-16 sm:px-8 sm:py-20 lg:py-24 lg:pe-14">
						<SectionIndex index="07" tone="dark">
							{t.register.eyebrow}
						</SectionIndex>
						<h2 className="mt-6 max-w-md font-display text-3xl font-bold leading-[1.1] text-white sm:text-4xl">
							{t.register.heading}
						</h2>
						<p className="mt-5 max-w-sm text-sm leading-relaxed text-white/60">
							{t.register.sub}
						</p>
						<div className="mt-9 bg-canvas p-6 sm:p-7">
							<LeadForm source="preview-a" trainingId={training.id} />
						</div>
					</div>

					<div className="relative hidden lg:block">
						<img
							src="/team/ayoub-training.webp"
							alt={t.register.imageAlt}
							width={560}
							height={686}
							className="h-full w-full object-cover opacity-90"
						/>
						<div
							aria-hidden
							className="absolute inset-0 bg-linear-to-l from-transparent to-navy rtl:bg-linear-to-r"
						/>
					</div>
				</div>
			</section>

			{/* ---------- footer ---------- */}
			<footer className="bg-navy-900 py-12">
				<div className="mx-auto max-w-6xl px-5 sm:px-8">
					<div className="flex flex-col items-center gap-6 border-b border-white/10 pb-8 text-center sm:flex-row sm:justify-between sm:text-start">
						<LogoMark className="h-9 w-auto" />
						<p className="max-w-xs text-sm text-white/50">{t.footer.tagline}</p>
						<a
							href="#register"
							className="rounded-full bg-teal px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-teal-600"
						>
							{t.footer.book}
						</a>
					</div>
					<div className="flex flex-col items-center justify-between gap-3 pt-6 text-xs text-white/40 sm:flex-row">
						<p>{t.footer.rights}</p>
						<p dir="ltr">moustachiracademy@moustachir.dz · www.moustachir.dz</p>
					</div>
				</div>
			</footer>

			<BookingFab href="#register" />
		</div>
	);
}
